import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Lock, Users, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBetaState } from "@/hooks/useBetaState";

export function BetaTab() {
  const { enabled, cap, remaining, primaryProfileCount, loading, error, refresh } = useBetaState();
  const [draftCap, setDraftCap] = useState<string>("");
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingCap, setSavingCap] = useState(false);

  useEffect(() => {
    if (!loading) setDraftCap(String(cap));
  }, [loading, cap]);

  const updateRow = async (patch: { enabled?: boolean; cap?: number }) => {
    const { error: updateError } = await supabase
      .from("beta_launch_state")
      .update(patch)
      .eq("id", true);
    if (updateError) {
      toast.error(updateError.message);
      return false;
    }
    return true;
  };

  const onToggle = async (next: boolean) => {
    setSavingToggle(true);
    const ok = await updateRow({ enabled: next });
    setSavingToggle(false);
    if (ok) {
      toast.success(next ? "Beta cap is now active" : "Beta cap disabled — signups open");
      await refresh();
    }
  };

  const onSaveCap = async () => {
    const parsed = Number.parseInt(draftCap, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.error("Cap must be a non-negative integer");
      return;
    }
    setSavingCap(true);
    const ok = await updateRow({ cap: parsed });
    setSavingCap(false);
    if (ok) {
      toast.success(`Cap updated to ${parsed}`);
      await refresh();
    }
  };

  const closed = enabled && remaining <= 0;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {closed ? <Lock className="h-5 w-5 text-primary" /> : <Sparkles className="h-5 w-5 text-primary" />}
          Launch beta cap
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-3">
          <Stat label="Status" value={enabled ? (closed ? "Full" : "Open") : "Disabled"} />
          <Stat label="Seats used" value={loading ? "…" : `${primaryProfileCount} / ${cap}`} icon={<Users className="h-4 w-4" />} />
          <Stat label="Seats remaining" value={loading ? "…" : String(remaining)} />
        </div>

        <div className="flex items-center justify-between rounded-md border border-border p-4">
          <div className="space-y-1">
            <Label htmlFor="beta-enabled" className="font-medium">Enforce signup cap</Label>
            <p className="text-sm text-muted-foreground">
              When enabled, new signups are blocked once primary profiles reach the cap.
            </p>
          </div>
          <Switch
            id="beta-enabled"
            checked={enabled}
            disabled={loading || savingToggle}
            onCheckedChange={onToggle}
          />
        </div>

        <div className="space-y-2 rounded-md border border-border p-4">
          <Label htmlFor="beta-cap" className="font-medium">Seat cap</Label>
          <p className="text-sm text-muted-foreground">
            The total number of primary accounts allowed during the beta. Existing accounts are never affected.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              id="beta-cap"
              type="number"
              min={0}
              step={1}
              className="max-w-xs"
              value={draftCap}
              onChange={(e) => setDraftCap(e.target.value)}
              disabled={loading || savingCap}
            />
            <div className="flex gap-2">
              <Button onClick={onSaveCap} disabled={loading || savingCap || draftCap === String(cap)}>
                {savingCap ? "Saving…" : "Save cap"}
              </Button>
              <Button variant="ghost" onClick={() => void refresh()} disabled={loading}>
                <RefreshCw className="mr-2 h-4 w-4" /> Refresh
              </Button>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          The cap is enforced server-side inside the signup transaction (<code>handle_new_user</code>), so it
          cannot be bypassed from the client. Admins always pass through.
        </p>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
